/**
 * PWA Diagnostics - Captures installation-related info for debugging
 * Logs are stored in device_error_logs table for admin review
 */

import { logError } from '@/hooks/useErrorLogging';

interface PWADiagnostics {
  isStandalone: boolean;
  displayMode: string;
  hasServiceWorker: boolean;
  swState: string | null;
  manifestLoaded: boolean;
  manifestError: string | null;
  installPromptAvailable: boolean;
  userAgent: string;
  platform: string;
  isSecureContext: boolean;
  protocol: string;
}

// Track if beforeinstallprompt was fired
let installPromptFired = false;
let deferredPrompt: Event | null = null;

const isPwaDebugEnabled = () => {
  try {
    return sessionStorage.getItem('pwa_debug_enabled') === '1';
  } catch {
    return false;
  }
};

/**
 * Collect PWA diagnostics information
 */
export async function collectPWADiagnostics(): Promise<PWADiagnostics> {
  const diagnostics: PWADiagnostics = {
    isStandalone: window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone === true,
    displayMode: getDisplayMode(),
    hasServiceWorker: 'serviceWorker' in navigator,
    swState: null,
    manifestLoaded: false,
    manifestError: null,
    installPromptAvailable: installPromptFired,
    userAgent: navigator.userAgent,
    platform: navigator.platform || 'unknown',
    isSecureContext: window.isSecureContext,
    protocol: window.location.protocol,
  };

  // Check service worker status
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        diagnostics.swState = registration.active?.state || 
                              registration.waiting?.state || 
                              registration.installing?.state || 
                              'no-worker';
      } else {
        diagnostics.swState = 'not-registered';
      }
    } catch (e) {
      diagnostics.swState = `error: ${e instanceof Error ? e.message : String(e)}`;
    }
  }

  // Check manifest
  try {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const manifestUrl = (manifestLink as HTMLLinkElement).href;
      const response = await fetch(manifestUrl);
      if (response.ok) {
        const manifest = await response.json();
        diagnostics.manifestLoaded = true;
        
        // Validate critical manifest fields
        const missingFields: string[] = [];
        if (!manifest.name) missingFields.push('name');
        if (!manifest.icons || manifest.icons.length === 0) missingFields.push('icons');
        if (!manifest.start_url) missingFields.push('start_url');
        if (!manifest.display) missingFields.push('display');
        
        if (missingFields.length > 0) {
          diagnostics.manifestError = `Missing fields: ${missingFields.join(', ')}`;
        }
        
        // Check for 192x192 and 512x512 icons (required for installability)
        const iconSizes = manifest.icons?.map((i: any) => i.sizes) || [];
        const has192 = iconSizes.some((s: string) => s?.includes('192'));
        const has512 = iconSizes.some((s: string) => s?.includes('512'));
        if (!has192 || !has512) {
          diagnostics.manifestError = (diagnostics.manifestError || '') + 
            ` Missing icon sizes: ${!has192 ? '192x192 ' : ''}${!has512 ? '512x512' : ''}`;
        }
      } else {
        diagnostics.manifestError = `HTTP ${response.status}`;
      }
    } else {
      diagnostics.manifestError = 'No manifest link found';
    }
  } catch (e) {
    diagnostics.manifestError = e instanceof Error ? e.message : String(e);
  }

  return diagnostics;
}

function getDisplayMode(): string {
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
  return 'browser';
}

/**
 * Initialize PWA diagnostic tracking
 * Call this early in app lifecycle
 */
export function initPWADiagnostics(): void {
  // Track beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    installPromptFired = true;
    deferredPrompt = e;
    if (isPwaDebugEnabled()) {
      console.log('[PWA] Install prompt available');
    }
    
    // Log success for debugging
    if (isPwaDebugEnabled()) {
      logError('PWA install prompt available', {
        type: 'pwa_diagnostic',
        event: 'beforeinstallprompt',
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Track when app is installed
  window.addEventListener('appinstalled', () => {
    if (isPwaDebugEnabled()) {
      console.log('[PWA] App installed');
      logError('PWA app installed successfully', {
        type: 'pwa_diagnostic',
        event: 'appinstalled',
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Log diagnostics on page load (delayed to allow SW to register)
  setTimeout(async () => {
    try {
      const diagnostics = await collectPWADiagnostics();
      
      // Only log if there are potential issues
      const hasIssues = !diagnostics.hasServiceWorker ||
                        diagnostics.swState === 'not-registered' ||
                        diagnostics.swState?.startsWith('error') ||
                        diagnostics.manifestError ||
                        !diagnostics.isSecureContext;
      
      if (hasIssues && isPwaDebugEnabled()) {
        logError('PWA diagnostics collected', {
          type: 'pwa_diagnostic',
          event: 'diagnostics',
          ...diagnostics,
        });
      }
      
      // Always log to console for debugging
      if (isPwaDebugEnabled()) {
        console.log('[PWA] Diagnostics:', diagnostics);
      }
    } catch (e) {
      console.error('[PWA] Failed to collect diagnostics:', e);
    }
  }, 5000); // Wait 5 seconds for SW to fully register
}

/**
 * Manually trigger diagnostic logging
 * Useful for debugging specific devices
 */
export async function logPWADiagnostics(): Promise<PWADiagnostics> {
  const diagnostics = await collectPWADiagnostics();
  if (isPwaDebugEnabled()) {
    logError('PWA manual diagnostic check', {
      type: 'pwa_diagnostic',
      event: 'manual_check',
      ...diagnostics,
    });
  }
  
  return diagnostics;
}

/**
 * Get the deferred install prompt if available
 */
export function getDeferredPrompt(): Event | null {
  return deferredPrompt;
}

/**
 * Check if install prompt is available
 */
export function isInstallPromptAvailable(): boolean {
  return installPromptFired;
}
